import React, { ComponentType, FunctionComponent, useCallback } from 'react';

import { MenuControl } from '@yandex-360/orb-components/dist/components/Menu2';
import cn from 'classnames';

import { i18n } from '../../../../helpers/i18n';
import { SettingsSectionKey } from '../../constants';
import {
    AccountSection,
    DevelopSection,
    HelpSection,
    PermissionsSection,
    SoundSection,
    SuspendedDiagnostics,
    ThemeSection,
    VideoSection,
    VirtualBackgroundSection,
} from '../components/sections';
import * as SettingsModal from '../components/settings-modal';
import { useShouldShowBandwidthTumbler } from '../hooks/use-should-show-bandwidth-tumbler';
import { SettingsV2Props } from '../types';
import css from './styles.css';

const SECTION_BY_KEY: Partial<Record<SettingsSectionKey, ComponentType>> = {
    [SettingsSectionKey.Permissions]: PermissionsSection,
    [SettingsSectionKey.VirtualBackground]: VirtualBackgroundSection,
    [SettingsSectionKey.Theme]: ThemeSection,
    [SettingsSectionKey.Help]: HelpSection,
    [SettingsSectionKey.Developers]: DevelopSection,
    [SettingsSectionKey.Account]: AccountSection,
    [SettingsSectionKey.Camera]: VideoSection,
    [SettingsSectionKey.Sound]: SoundSection,
    [SettingsSectionKey.Diagnostics]: SuspendedDiagnostics,
};

export const SettingsV2Desktop: FunctionComponent<SettingsV2Props> = ({
    parentRef,
    visible,
    onClose,
    onChangeSelectedItemId,
    selectedItemId,
    items,
    ...props
}) => {
    const onControlPress = useCallback(
        (key: SettingsSectionKey, onPress: Function) => () => {
            onChangeSelectedItemId?.(key);
            onPress();
        },
        [onChangeSelectedItemId]
    );

    const hasExpButton = useShouldShowBandwidthTumbler();

    const Section = selectedItemId ? SECTION_BY_KEY[selectedItemId] : null;

    return (
        <SettingsModal.Modal
            {...props}
            testId="settings-modal"
            title={i18n('settings-v2', 'title')}
            className={css.modal}
            visible={visible}
            onClose={onClose}
            size="xl"
            scope={parentRef}
        >
            <SettingsModal.Menu testId="settings-menu" className={css.menu} items={items} showTooltip="auto">
                {({ item, onPress, ...props }) => (
                    <MenuControl
                        {...props}
                        item={item}
                        isSelected={item.id === selectedItemId}
                        onPress={onControlPress(item.id as SettingsSectionKey, onPress)}
                    />
                )}
            </SettingsModal.Menu>
            <SettingsModal.Content className={cn(css.content, { [css.withExpButton]: hasExpButton })}>
                {Section && <Section />}
            </SettingsModal.Content>
        </SettingsModal.Modal>
    );
};
